import React, { Component } from 'react';
import {
  inject,
  observer,
} from 'mobx-react';
import ReactTable from 'react-table';
import {
  Button,
  Form,
  FormControl,
  FormGroup,
} from 'react-bootstrap';
import getSortHeaderProps from '../../../utils/get-sorted-headers';
import { toNumber } from '../../../utils/numbers';
import { withTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { Loader } from '../../../components/PageLoading/PageLoading';
import cx from 'classnames';

class Reconciliation extends Component {
  constructor(props) {
    super(props);
    this.mounted = false;
    this.state = {
      loading: true,
      sorted: [],
      data: [],
      dataWithDetails: [],
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      inventoryItemId: null,
      withDetails: false,
      error: false,
    };
    this.months = ['', 'January','February','March','April','May','June','July','August','September','October','November','December'];
  }

  componentDidMount () {
    this.mounted = true;
    this.getData();
  }

  componentWillUnmount () {
    this.mounted = false;
  }

  getData() {
    const { materialStore, notifierStore } = this.props;
    const { month, year, inventoryItemId } = this.state;

    this.setState({
      loading: true,
    });

    materialStore.getReconciliation(month, year, inventoryItemId)
      .then(data => {
        if (this.mounted) {
          const dataWithDetails = this.getDataWithDetails(data);
          this.setState({
            data,
            dataWithDetails,
            loading: false,
          });
        }
      })
      .catch(e => {
        notifierStore.showError({ key: 'Can\'t load reconciliation data:', props: { error: e.message }});
        if (this.mounted) {
          this.setState({
            loading: false,
          });
        }
      });
  }

  get isCurrentMonthYear() {
    const { month, year } = this.state;
    const date = new Date();
    return Number(month) === date.getMonth() + 1 && Number(year) === date.getFullYear();
  }

  get isReconciled() {
    const { data } = this.state;
    return data.find(v => v.reconciled);
  }

  get canReconcile() {
    return this.isCurrentMonthYear && !this.isReconciled;
  }

  get canUndoReconcile() {
    return this.isCurrentMonthYear && this.isReconciled;
  }

  getColumns() {
    const { sorted, error } = this.state;
    return [
      {
        headerText: 'Name',
        id: 'itemCode',
        accessor: d => d,
        Cell: row => `${row.value.binName ? row.value.binName : row.value.itemCode}`,
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: 'Opening',
        id: 'opening',
        accessor: d => d,
        Cell: row => (
          row.value.binName ? '' :
            `${toNumber(row.value.opening, 2)} ${row.value.inventoryUnitSymbol || ''}`
        ),
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: 'Incoming',
        id: 'incoming',
        accessor: d => d,
        Cell: row => (
          row.value.binName ? '' :
            `${toNumber(row.value.incoming, 2)} ${row.value.inventoryUnitSymbol || ''}`
        ),
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: 'Auto',
        id: 'autoUsed',
        accessor: d => d,
        Cell: row => `${toNumber(row.value.autoUsed, 2)} ${row.value.inventoryUnitSymbol || ''}`,
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: 'Manual',
        id: 'manualUsed',
        accessor: d => d,
        Cell: row => `${toNumber(row.value.manualUsed, 2)} ${row.value.inventoryUnitSymbol || ''}`,
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: 'Adjusted',
        id: 'adjusted',
        accessor: d => d,
        Cell: row => (
          row.value.binName ? '' :
            `${toNumber(row.value.adjusted, 2)} ${row.value.inventoryUnitSymbol || ''}`
        ),
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: 'Closing',
        id: 'closing',
        accessor: d => d,
        Cell: row => (
          row.value.binName ? '' :
            `${row.value.reconciled ? toNumber(row.value.physical, 2) : toNumber(row.value.closing, 2)}
            ${row.value.inventoryUnitSymbol || ''}`
        ),
        ...getSortHeaderProps(sorted),
      },
      {
        Header: 'Physical',
        sortable: false,
        Cell: row => (
          row.original.binName ? '' :
            this.canReconcile ? (
              <div className={cx({'has-error': !row.original.physical && error })}>
                <FormControl
                  name="physical"
                  type="number"
                  value={row.original.physical || ''}
                  onChange={e => this.onQuantityChange(row.original.inventoryItemId, e.target.value)}
                  bsSize="sm"
                />
              </div>
            )
              : `${toNumber(row.original.physical || 0, 2)} ${row.original.inventoryUnitSymbol || ''}`
        ),
      },
      {
        headerText: 'Gain/Loss',
        accessor: d => d,
        id: 'reconciled',
        Cell: row => (
          row.value.binName ? '' :
            `${ row.value.reconciled ? toNumber(row.value.reconciled, 2) :  row.value.physical ? toNumber(row.value.physical - row.value.closing, 1) : 0} ${row.value.inventoryUnitSymbol || ''}`
        ),
        ...getSortHeaderProps(sorted),
      },
    ];
  };

  reconcile() {
    const { materialStore, notifierStore } = this.props;
    const { data, year, month } = this.state;

    if (data.find(v => !v.physical)) {
      notifierStore.showError('Physical values are required!');
      this.setState({ error: true });
      return;
    }

    this.setState({ error: false });

    materialStore.saveReconciliation(year, month, data)
      .then(() => {
        if (this.mounted) {
          this.getData();
          materialStore.onMaterialUpdate();
        }
      })
      .catch(e => notifierStore.showError(e.message));
  };

  undoReconcile() {
    const { materialStore, notifierStore } = this.props;
    const { year, month } = this.state;

    materialStore.undoReconcile(year, month)
      .then(() => {
        if (this.mounted) {
          this.getData();
        }
      })
      .catch(e => notifierStore.showError(e.message));
  }

  onQuantityChange(inventoryItemId, physical) {
    const { data } = this.state;
    const payload = data.slice();

    payload.forEach(v => {
      if (v.inventoryItemId === inventoryItemId) {
        v.physical = physical;
      }
    });

    this.setState({
      data: payload,
      error: false,
    });

  };

  handleYearChange = e => {
    this.setState({
      year: e.target.value,
      error: false,
    });

    setTimeout(() => { this.getData(); }, 0);
  };

  handleMonthChange = e => {
    this.setState({
      month: e.target.value,
      error: false,
    });

    setTimeout(() => { this.getData(); }, 0);
  };

  getDataWithDetails(data) {
    const { devicesStore } = this.props;
    let withDetails = [];

    data.forEach(v => {
      withDetails.push(v);

      if (v.usedPerBin && v.usedPerBin.length) {
        v.usedPerBin.forEach(b => {
          const bin = devicesStore.getDeviceById(b.binId);
          withDetails.push({
            ...v,
            binId: b.binId,
            binName: bin ? bin.description : b.binId,
            used: b.used,
            autoUsed: b.autoUsed,
            manualUsed: b.manualUsed,
          });
        });
      }

    });

    return withDetails;
  }

  showInventoryPerBin() {
    this.setState({
      withDetails: !this.state.withDetails,
    });
  }

  printReconciliation() {
    const { printStore, settingsStore, locationsStore } = this.props;
    const { data, month, year, loading } = this.state;
    if (loading) {
      return;
    }

    const timezone = settingsStore.configuration.ianaTimeZone;
    const d = {
      reconciliation: data,
      printDate: moment().tz(timezone).format('LLL'),
      plant: locationsStore.currentPlant.description,
      location: locationsStore.currentLocation.name,
      month: this.months[month],
      year,
    };

    const html = document.querySelector('.reconciliation-table .rt-table').innerHTML;
    printStore.printHtml(`
      <h2>${d.plant}, ${d.location}</h2>
      <h2>${d.month} ${year}</h2>
      ${html}
      <h5>${d.printDate}</h5>
      `);
  }

  render() {
    const { t } = this.props;
    const { sorted, data, month, year, withDetails, dataWithDetails } = this.state;
    const date = new Date();
    const years = Array(2).fill(date.getFullYear());

    return (
      !data.length ? (
        <Loader/>
      ) : (
        <div>
          <Button className="btn btn-sm btn-simple btn-action pull-right"
            onClick={() => this.printReconciliation()}>
            <i className="fa fa-print"/> {t('Print')}
          </Button>

          <Form inline className={'reconciliation-dates'}>
            <FormGroup>
              <FormControl
                name="month"
                componentClass="select"
                value={month}
                onChange={this.handleMonthChange}
                bsSize="sm"
              >
                {this.months.map((option, i) => (
                  <option key={i} value={i}>
                    {option}
                  </option>
                ))}
              </FormControl>
            </FormGroup>
            <FormGroup>
              <FormControl
                name="year"
                componentClass="select"
                value={year}
                onChange={this.handleYearChange}
                bsSize="sm"
              >
                {years.map((option, i) => (
                  <option key={i} value={option - i}>
                    {option - i}
                  </option>
                ))}
              </FormControl>
            </FormGroup>

            <Button className="btn btn-sm btn-info btn-action margin-right-10"
              onClick={() => this.showInventoryPerBin()}>
              {!withDetails ? t('Show More') : t('Show Less')}
            </Button>

            {this.canReconcile && (
              <Button className="btn btn-sm btn-primary btn-action"
                onClick={() => this.reconcile()}>
                {t('Reconcile')}
              </Button>
            )}

            {this.canUndoReconcile && (
              <Button className="btn btn-sm btn-primary btn-action"
                onClick={() => this.undoReconcile()}>
                {t('Undo Reconcile')}
              </Button>
            )}

          </Form>

          <ReactTable
            className="-highlight max-height-table reconciliation-table"
            data={withDetails ? dataWithDetails : data}
            columns={this.getColumns()}
            sorted={sorted}
            onSortedChange={sorted => this.setState({ sorted })}
            showPaginationTop={false}
            showPaginationBottom={false}
            showPageSizeOptions={false}
            resizable={false}
            minRows={1}
            pageSize={50}
            getTrProps={(state, rowInfo) => {
              if (rowInfo && rowInfo.original) {
                return {
                  className: rowInfo.original.binName ? 'extra-data' : '',
                };
              }
              return {};
            }}
          />

        </div>
      )
    );
  }
}

export default withTranslation()(inject('materialStore', 'notifierStore', 'printStore', 'devicesStore', 'settingsStore', 'locationsStore')(observer(Reconciliation)));
